import React from 'react';
import './Textarea.scss';
import { Input } from 'antd';
import classNames from 'classnames';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import type { DataAttributes } from '@/components/utils/TypeUtils';

const { TextArea } = Input;

interface TextareaOnChangeData {
  /** Updated input value from the textarea */
  value: string;
}

type TextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  /** The rest of these native props have customized definitions. */
  'defaultValue' | 'onChange' | 'value' | 'onResize'
> & {
  /**
   * Id attribute of the textarea
   */
  id?: string;
  /**
   * Default value of the textarea. Provide this if the textarea should be an uncontrolled component
   * which tracks its current state internally; otherwise, use `value`.
   */
  defaultValue?: string;
  /**
   * Current value of the textarea. Provide this if the textarea is a controlled component where you
   * are maintaining its current state; otherwise, use `defaultValue`.
   */
  value?: string;
  /**
   * The name of the textarea
   */
  name?: string;
  /**
   * Custom CSS classed to be applied
   */
  className?: string;
  /**
   * Whether to enable autocomplete or not
   */
  autoComplete?: 'on' | 'off';
  /**
   * Whether to disable the textarea or not
   */
  disabled?: boolean;
  /**
   * Number of rows that the textarea should have
   */
  rows?: number;
  /**
   * Maximum length of characters that can be entered
   */
  maxLength?: number;
  /**
   * Placeholder text for the textarea
   */
  placeholder?: string;
  /**
   * To show the count of the characters entered/max count
   */
  showCount?: boolean;
  /**
   * Size of the textarea (changes the font size, width and padding).
   */
  variant?: 'small' | 'medium' | 'large';
  /**
   * The data-* attribute is used to store custom data private to the page or application.
   */
  dataAttributes?: DataAttributes;
  /**
   * Called when the user changes the textarea value.
   */
  onChange?: (ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => void;
  /** Field level error message */
  fieldErrMessg?: string;
};

const Textarea: React.FC<TextareaProps> = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    const {
      id,
      placeholder,
      value,
      defaultValue,
      name,
      className,
      autoComplete,
      disabled,
      rows,
      maxLength,
      showCount,
      variant,
      dataAttributes,
      onChange,
      fieldErrMessg,
      ...rest
    } = props;

    const textAreaClass = classNames(
      'textarea',
      className,
      maxLength && showCount && 'textarea--with-count',
      disabled && 'textarea--disabled',
      variant && `textarea--${variant}`
    );

    const [inputValue, setInputValue] = React.useState(defaultValue ?? value ?? '');

    /**
     * Update the textarea's value when the user changes it.
     */
    const handleChange = React.useCallback(
      (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = ev.target.value;
        setInputValue(newValue);
        onChange?.(ev, { value: newValue });
      },
      [onChange]
    );

    return (
      <div className={`${textAreaClass} ${fieldErrMessg ? 'textarea-error' : ''}`}>
        <TextArea
          ref={ref}
          id={id}
          name={name}
          defaultValue={defaultValue}
          value={value ?? inputValue}
          autoComplete={autoComplete}
          disabled={disabled}
          showCount={maxLength ? showCount : false}
          rows={rows ?? 5}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={handleChange}
          {...getPrefixedDataAttributes(dataAttributes, 'Textarea')}
          {...rest}
        />

        <div role="alert" className="err-messg-container">
          {fieldErrMessg}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

Textarea.defaultProps = {
  placeholder: 'e.g. Turn Phi mu into AEPi, change the blue color to dark yellow.',
  autoComplete: 'off',
  disabled: false,
};

export default Textarea;
